import dayjs from 'dayjs';
import { RecurringPeriod } from 'src/modules/campaigns/types/RecurringSettings';
import {
  RECURRING_START_TIME,
  RECURRING_END_TIME,
} from 'src/modules/campaigns/helpers';
import { RecurringTemplateSettings, TemplateFieldControl } from '../types';

/**
 * Fields the backend mandates for recurring campaigns. Their Visible/Required
 * toggles are hidden in the config builder and forced on by normalize().
 */
export const REQUIRED_RECURRING_TEMPLATE_FIELDS: (keyof RecurringTemplateSettings)[] = [
  'period',
  'interval',
  'daysOfWeek',
  'dateStart',
];

const control = <T>(value: T, overrides?: Partial<TemplateFieldControl<T>>): TemplateFieldControl<T> => ({
  visible: true,
  required: false,
  default: value,
  ...overrides,
});

/**
 * Default template-level recurring settings.
 * Defaults mirror the runtime createDefaultRecurringSettings() shape.
 */
export const createDefaultRecurringTemplateSettings = (): RecurringTemplateSettings => ({
  visible: true,
  period: control<RecurringPeriod>('day', { required: true }),
  interval: control<number>(1, { required: true }),
  startTime: control<string>(RECURRING_START_TIME),
  endTime: control<string>(RECURRING_END_TIME),
  daysOfWeek: control<number[]>([], { required: true }),
  dateStart: control<string>('', { required: true }),
  dateEnd: control<string>(''),
});

const normalizeControl = <T>(
  raw: Partial<TemplateFieldControl<T>> | undefined,
  fallback: TemplateFieldControl<T>,
  forceOn = false,
): TemplateFieldControl<T> => ({
  visible: forceOn ? true : typeof raw?.visible === 'boolean' ? raw.visible : fallback.visible,
  required: forceOn ? true : typeof raw?.required === 'boolean' ? raw.required : fallback.required,
  default: raw?.default !== undefined ? (raw.default as T) : fallback.default,
  label: raw?.label ?? fallback.label,
});

const isRequiredField = (key: keyof RecurringTemplateSettings) =>
  REQUIRED_RECURRING_TEMPLATE_FIELDS.includes(key);

/**
 * Coerce a partial / loaded recurring settings block into the full shape.
 * Backend-mandated fields are forced visible + required regardless of stored
 * value, so a stale/old config can never hide a field the backend needs.
 */
export const normalizeRecurringTemplateSettings = (
  settings?: Partial<RecurringTemplateSettings> | null,
): RecurringTemplateSettings => {
  const defaults = createDefaultRecurringTemplateSettings();
  return {
    visible: typeof settings?.visible === 'boolean' ? settings.visible : defaults.visible,
    period: normalizeControl(settings?.period, defaults.period, isRequiredField('period')),
    interval: normalizeControl(settings?.interval, defaults.interval, isRequiredField('interval')),
    startTime: normalizeControl(settings?.startTime, defaults.startTime),
    endTime: normalizeControl(settings?.endTime, defaults.endTime),
    daysOfWeek: normalizeControl(settings?.daysOfWeek, defaults.daysOfWeek, isRequiredField('daysOfWeek')),
    dateStart: normalizeControl(settings?.dateStart, defaults.dateStart, isRequiredField('dateStart')),
    dateEnd: normalizeControl(settings?.dateEnd, defaults.dateEnd),
  };
};

/**
 * Validate the template's default values.
 * Only the date-ordering rule applies at template level — required-presence is
 * enforced at runtime, since the admin may intentionally leave defaults blank.
 */
export const validateRecurringTemplateSettings = (
  settings?: RecurringTemplateSettings | null,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!settings) return errors;

  const start = settings.dateStart.default ? dayjs(settings.dateStart.default) : null;
  const end = settings.dateEnd.default ? dayjs(settings.dateEnd.default) : null;

  if (start?.isValid() && end?.isValid() && end.isBefore(start)) {
    errors['recurringSettings.dateEnd'] = 'End date cannot be before start date.';
  }

  return errors;
};
